/**
 * Centralised UK-time datetime formatting.
 *
 * The backend stores all timestamps in UTC. These helpers convert to
 * Europe/London (BST in summer, GMT in winter) and pin the locale to
 * en-GB so users in any browser timezone see consistent UK time.
 *
 * Pick the variant that matches the visual format you need:
 *   formatDateTime       → "27 Apr 2026, 15:32"
 *   formatDateTimeAt     → "27 Apr 2026 at 15:32"
 *   formatDateTimeSmart  → "Today at 15:32" / "Yesterday at 15:32" / fallback
 *   formatDate           → "27 Apr 2026"
 *   formatShortWeekdayDate → "Mon 27 Apr"
 *   formatDateNumeric    → "27/04/2026"
 *   formatDateTimeFull   → "27/04/2026, 15:32:00"
 */

const UK_TIMEZONE = "Europe/London";
const UK_LOCALE = "en-GB";

export function formatUKDateKey(date: Date): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    timeZone: UK_TIMEZONE,
  }).formatToParts(date);
  const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${values.year}-${values.month}-${values.day}`;
}

export function addDaysToUKDateKey(dateKey: string, days: number): string {
  const [year, month, day] = dateKey.split("-").map(Number);
  const shifted = new Date(Date.UTC(year, month - 1, day + days, 12));
  return formatUKDateKey(shifted);
}

/** "27 Apr 2026, 15:32" — short month, 24-hour time. Most common. */
export function formatDateTime(dateString: string): string {
  return new Intl.DateTimeFormat(UK_LOCALE, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: UK_TIMEZONE,
  }).format(new Date(dateString));
}

/** "27 Apr 2026 at 15:32" — engineer-mobile style with "at" separator. */
export function formatDateTimeAt(dateString: string): string {
  const date = new Date(dateString);
  const datePart = new Intl.DateTimeFormat(UK_LOCALE, {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: UK_TIMEZONE,
  }).format(date);
  const timePart = new Intl.DateTimeFormat(UK_LOCALE, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: UK_TIMEZONE,
  }).format(date);
  return `${datePart} at ${timePart}`;
}

/** "Today at 15:32" / "Yesterday at 15:32" / "27 Apr 2026 at 15:32" */
export function formatDateTimeSmart(dateString: string): string {
  const date = new Date(dateString);
  const today = new Date();

  const timeStr = new Intl.DateTimeFormat(UK_LOCALE, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: UK_TIMEZONE,
  }).format(date);

  // Compare against UK-local calendar dates so "Today" works correctly
  // for users whose browser timezone differs from London.
  const dateLocal = formatUKDateKey(date);
  const todayLocal = formatUKDateKey(today);
  const yesterdayLocal = addDaysToUKDateKey(todayLocal, -1);

  if (dateLocal === todayLocal) return `Today at ${timeStr}`;
  if (dateLocal === yesterdayLocal) return `Yesterday at ${timeStr}`;

  const dateStr = new Intl.DateTimeFormat(UK_LOCALE, {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: UK_TIMEZONE,
  }).format(date);
  return `${dateStr} at ${timeStr}`;
}

/** "27 Apr 2026" — date only, short month. */
export function formatDate(dateString: string): string {
  return new Intl.DateTimeFormat(UK_LOCALE, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    timeZone: UK_TIMEZONE,
  }).format(new Date(dateString));
}

/**
 * "Wed 8 May" — short weekday, day and short month. The abbreviations are the
 * browser's own ("Sept" in Chrome, "Sep" in Safari), so every screen that uses
 * this helper spells a date the same way on a given device.
 */
export function formatShortWeekdayDate(date: Date): string {
  return new Intl.DateTimeFormat(UK_LOCALE, {
    weekday: "short",
    day: "numeric",
    month: "short",
    timeZone: UK_TIMEZONE,
  }).format(date);
}

/** "May" — short month, abbreviated as formatShortWeekdayDate does. */
export function formatShortMonth(date: Date): string {
  return new Intl.DateTimeFormat(UK_LOCALE, {
    month: "short",
    timeZone: UK_TIMEZONE,
  }).format(date);
}

/** "27/04/2026" — date only, numeric. */
export function formatDateNumeric(dateString: string): string {
  return new Intl.DateTimeFormat(UK_LOCALE, {
    timeZone: UK_TIMEZONE,
  }).format(new Date(dateString));
}

/** "27/04/2026, 15:32:00" — full numeric date + time with seconds. */
export function formatDateTimeFull(dateString: string): string {
  return new Date(dateString).toLocaleString(UK_LOCALE, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
    timeZone: UK_TIMEZONE,
  });
}

/**
 * "Today" / "Tomorrow" / "Wed, 8 May" / "8 May 2026" — picks the most
 * compact form for an upcoming delivery or collection date.
 *
 * - Same UK calendar date as today → "Today"
 * - Next UK calendar date → "Tomorrow"
 * - Within the next 7 UK days → "Wed, 8 May"
 * - Beyond that (or in the past) → "8 May 2026"
 *
 * Returns null for missing or unparseable input so callers can hide the
 * line without an extra check. Suppliers that don't surface an earliest
 * date pass null and the calling component renders nothing.
 */
/**
 * Backorder availability label: "Available on 9th October" (year appended only
 * when it isn't the current year). Backorder-only wording — branch/DC dates
 * keep formatEarliestDate. Returns null when the date is missing/invalid.
 */
export function formatBackorderAvailability(
  dateString: string | null | undefined,
): string | null {
  if (!dateString) return null;
  const parsed = new Date(dateString);
  if (Number.isNaN(parsed.getTime())) return null;
  const parts = new Intl.DateTimeFormat(UK_LOCALE, {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: UK_TIMEZONE,
  }).formatToParts(parsed);
  const day = Number(parts.find((part) => part.type === "day")?.value ?? "0");
  const month = parts.find((part) => part.type === "month")?.value ?? "";
  const year = parts.find((part) => part.type === "year")?.value ?? "";
  const remainder = day % 100;
  const suffix =
    remainder >= 11 && remainder <= 13
      ? "th"
      : ["th", "st", "nd", "rd"][day % 10 <= 3 ? day % 10 : 0];
  const currentYear = new Intl.DateTimeFormat(UK_LOCALE, {
    year: "numeric",
    timeZone: UK_TIMEZONE,
  }).format(new Date());
  return `Available on ${day}${suffix} ${month}${year !== currentYear ? ` ${year}` : ""}`;
}

export function formatEarliestDate(dateString: string | null | undefined): string | null {
  if (!dateString) return null;

  const parsed = new Date(dateString);
  if (Number.isNaN(parsed.getTime())) return null;

  const todayKey = formatUKDateKey(new Date());
  const targetKey = formatUKDateKey(parsed);

  if (targetKey === todayKey) return "Today";
  if (targetKey === addDaysToUKDateKey(todayKey, 1)) return "Tomorrow";

  // Within the next 7 days → short weekday + day + short month
  for (let dayOffset = 2; dayOffset <= 7; dayOffset += 1) {
    if (targetKey === addDaysToUKDateKey(todayKey, dayOffset)) {
      return formatShortWeekdayDate(parsed);
    }
  }

  // Outside the 7-day window — fall back to the standard date formatter
  return formatDate(dateString);
}

/**
 * "today" / "tomorrow" / "on 18 Oct" — when stock can be had, written to sit
 * mid-sentence ("10 available today"). UK calendar days. Null for a missing
 * or unparseable date.
 */
export function formatAvailableWhen(dateString: string | null | undefined): string | null {
  if (!dateString) return null;

  const parsed = new Date(dateString);
  if (Number.isNaN(parsed.getTime())) return null;

  const todayKey = formatUKDateKey(new Date());
  const targetKey = formatUKDateKey(parsed);

  if (targetKey === todayKey) return "today";
  if (targetKey === addDaysToUKDateKey(todayKey, 1)) return "tomorrow";
  return `on ${formatDayShortMonth(dateString)}`;
}

/** "18 Oct" — day and short month, UK calendar. Null for a missing or unparseable date. */
export function formatDayShortMonth(dateString: string | null | undefined): string | null {
  if (!dateString) return null;
  const parsed = new Date(dateString);
  if (Number.isNaN(parsed.getTime())) return null;
  return new Intl.DateTimeFormat(UK_LOCALE, {
    day: "numeric",
    month: "short",
    timeZone: UK_TIMEZONE,
  }).format(parsed);
}

/** "27 Apr 2026" and "15:32" from a timestamp (UK time), for two-line cells. */
export function splitDateTime(dateString: string): { date: string; time: string } {
  const formatted = formatDateTime(dateString)
  const commaIndex = formatted.indexOf(',')
  if (commaIndex === -1) return { date: formatted, time: '' }
  return { date: formatted.slice(0, commaIndex).trim(), time: formatted.slice(commaIndex + 1).trim() }
}
